import { useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import {
	Home,
	Target,
	LayoutGrid,
	Bell,
	MoreHorizontal,
} from "lucide-react";
import { useUnreadCount } from "../../hooks/queries/useNotificationQueries";
import { useSocialDrafts } from "../../hooks/queries/useSocialDraftQueries";
import { usePro } from "../../lib/pro-context";
import { MoreSheet } from "./more-sheet";

const NAV_ITEMS = [
	{ to: "/", icon: Home, label: "Home", exact: true },
	{ to: "/crm", icon: Target, label: "CRM", exact: false },
	{ to: "/projects", icon: LayoutGrid, label: "Projects", exact: false },
] as const;

export function BottomNav({ visible }: { visible: boolean }) {
	const [moreOpen, setMoreOpen] = useState(false);
	const routerState = useRouterState();
	const { data: unreadCount = 0 } = useUnreadCount();
	const { proId } = usePro();
	const { data: socialDraftsData } = useSocialDrafts(proId ?? null);
	const socialPendingDraftCount = socialDraftsData?.pendingCount ?? 0;
	const pathname = routerState.location.pathname;

	const moreRoutes = [
		"/analytics",
		"/profile",
		"/team",
		"/website",
		"/blogs",
		"/settings",
		"/social-studio",
	];
	const isMoreActive = moreRoutes.some((r) => pathname.startsWith(r));
	const isNotificationsActive = pathname.startsWith("/notifications");

	return (
		<>
			<nav
				className={`fixed inset-x-0 bottom-0 z-50 border-t border-border-default bg-background-elevated pb-safe sm:hidden transition-transform duration-200 ${
					visible ? "translate-y-0" : "translate-y-full"
				}`}
				aria-label="Main navigation"
			>
				<ul className="flex h-14 items-stretch">
					{NAV_ITEMS.map(({ to, icon: Icon, label, exact }) => {
						const isActive = exact
							? pathname === to
							: pathname.startsWith(to);
						return (
							<li key={to} className="flex-1">
								<Link
									to={to}
									className={`flex h-full flex-col items-center justify-center gap-0.5 text-[10px] transition-colors ${
										isActive
											? "text-primary-700 font-semibold"
											: "text-foreground-muted"
									}`}
								>
									<Icon
										className="h-5 w-5"
										fill={isActive ? "currentColor" : "none"}
									/>
									{label}
								</Link>
							</li>
						);
					})}

					{/* Notifications */}
					<li className="flex-1">
						<Link
							to="/notifications"
							className={`flex h-full flex-col items-center justify-center gap-0.5 text-[10px] transition-colors relative ${
								isNotificationsActive
									? "text-primary-700 font-semibold"
									: "text-foreground-muted"
							}`}
						>
							<div className="relative">
								<Bell
									className="h-5 w-5"
									fill={
										isNotificationsActive ? "currentColor" : "none"
									}
								/>
								{unreadCount > 0 && (
									<span className="absolute -top-1.5 -right-2 bg-error text-white text-[8px] font-bold px-1 py-0.5 rounded-full min-w-[14px] text-center leading-tight">
										{unreadCount > 99 ? "99+" : unreadCount}
									</span>
								)}
							</div>
							Notifications
						</Link>
					</li>

					{/* More */}
					<li className="flex-1">
						<button
							type="button"
							onClick={() => setMoreOpen(true)}
							className={`flex h-full w-full flex-col items-center justify-center gap-0.5 text-[10px] transition-colors ${
								isMoreActive
									? "text-primary-700 font-semibold"
									: "text-foreground-muted"
							}`}
						>
							<div className="relative">
								<MoreHorizontal
									className="h-5 w-5"
									fill={isMoreActive ? "currentColor" : "none"}
								/>
								{socialPendingDraftCount > 0 && (
									<span className="absolute -top-1.5 -right-2 h-2 w-2 rounded-full bg-primary-600" />
								)}
							</div>
							More
						</button>
					</li>
				</ul>
			</nav>

			<MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} />
		</>
	);
}
